<template>
  <!--
    [3차] school 데이터 로드 완료(schoolLoaded === true) 전에는 본문 lazy 마운트.
    이전에는 school: {} 초기값으로 PageHeader/StatusStepper/FormCard들이 마운트되고,
    이후 GET_SCHOOL_DETAIL 응답으로 school이 통째로 교체되면서
    reactive 의존이 재계산되는 과정에 v-calendar/Vuetify teleport patch가 깨질 여지가 있었음.
  -->
  <div class="page-wrap" v-if="schoolLoaded">
    <!-- 페이지 헤더 + 학교 정보(인라인) -->
    <div class="header-info-row">
      <div class="title-col">
        <div class="title-line">
          <PageHeader
            :title="schoolTitle"
          />
          <!-- 내부용(관리용) 총괄 — ID 규칙 lms{학교코드}00으로 판별, 전달 메일 제외 대상.
               기존 engLmsCode 바인딩은 API 응답에 없는 필드라 항상 "-"였음 (qa-incomplete-1021 #4) -->
          <span class="master-account-badge">
            내부용 LMS <span class="mono">{{ school.masterAccounts?.internal?.id || "-" }}</span>
          </span>
        </div>

      <div class="school-info-inline">
        <table class="data-table school-info-table">
          <tr>
            <td class="th" style="width: 130px">교육청</td>
            <td class="th" style="width: 120px">학교명</td>
            <td class="th" style="width: 70px">플랜</td>
            <td class="th" style="width: 120px">계정발급 상태</td>
            <td class="th" style="width: 110px">분류</td>
            <td class="th" style="width: 90px">계약 학생 수</td>
            <td class="th tdlast" style="width: 160px">
              <span class="memo-cell">
                메모
                <button
                  type="button"
                  class="memo-history-btn"
                  title="메모 기록 보기"
                  @click="memoHistoryOpen = true"
                >기록</button>
              </span>
            </td>
          </tr>
          <tr>
            <td class="td">
              <select v-model="school.office" class="cell-input cell-select">
                <option value="">미지정</option>
                <option v-for="o in officeOptions" :key="o" :value="o">{{ o }}</option>
              </select>
            </td>
            <td class="td">
              <input
                v-model="school.schoolName"
                type="text"
                spellcheck="false"
                placeholder="학교 이름"
                class="cell-input"
              />
            </td>
            <td
              @click.stop="planChangePopup($event)"
              class="td td-clickable"
            >
              <div class="td-select">
                <span>{{ displayPlan(school.payPlan) }}</span>
                <span class="td-caret" aria-hidden="true">▼</span>
              </div>
            </td>
            <td
              @click.stop="goaccount($event)"
              class="td td-clickable"
            >
              <div class="td-select">
                <StatusBadge v-if="school.accountStatus" :status="school.accountStatus" :dot="false" variant="text" />
                <span v-else>-</span>
                <span class="td-caret" aria-hidden="true">▼</span>
              </div>
            </td>
            <td
              @click.stop="gotype($event)"
              class="td td-clickable"
            >
              <!-- 유료 학교의 빈 분류는 DB 변경(819건 마이그레이션) 없이 화면에서만
                   "유료"로 표시한다 (B안 — schoolType.js 상단 주석 참조) -->
              <div class="td-select">
                <span>{{ school.type || "유료" }}</span>
                <span class="td-caret" aria-hidden="true">▼</span>
              </div>
            </td>
            <td class="td">{{ currentYearContractStudentNumber ?? "-" }}</td>
            <td class="td tdlast">
              <input
                v-model="school.memo"
                type="text"
                spellcheck="false"
                placeholder="메모 입력"
                class="cell-input"
              />
            </td>
          </tr>
        </table>

        <div v-if="schoolInfoDirty" class="school-info-actions">
          <v-btn
            @click="updateSchoolInfo()"
            variant="flat"
            size="small"
            class="btn-primary"
          >저장</v-btn>
        </div>
      </div>
      </div>

      <div class="detail-actions">
        <div class="password-reset">
          <span class="password-reset__label">총괄 계정 발급</span>
          <input
            v-model="masterAccountName"
            type="text"
            spellcheck="false"
            placeholder="이름 (미입력 시 총괄)"
            class="cell-input"
          />
          <v-btn
            @click="issueMasterAccount()"
            variant="outlined"
            size="small"
            class="btn-secondary"
          >발급</v-btn>
        </div>
        <div class="password-reset">
          <span class="password-reset__label">비밀번호 변경</span>
          <input
            v-model="resetPasswordId"
            type="text"
            spellcheck="false"
            placeholder="ID 입력"
            class="cell-input"
          />
          <v-btn
            @click="resetStudentPassword()"
            variant="outlined"
            size="small"
            class="btn-secondary"
          >초기화</v-btn>
        </div>
        <div class="password-reset">
          <span class="password-reset__label">선생님 정보 초기화</span>
          <input
            v-model="resetTeacherInfoId"
            type="text"
            spellcheck="false"
            placeholder="ID 입력"
            class="cell-input"
          />
          <v-btn
            @click="resetTeacherInfo()"
            variant="outlined"
            size="small"
            class="btn-secondary"
          >초기화</v-btn>
        </div>
      </div>
    </div>

    <!-- 계약 정보 -->
    <FormCard class="section-card">
      <template #header>
        <div class="card-header-row">
          <span>계약 정보</span>
          <div class="card-header-actions">
            <span class="usage-add__label">사용 인원 추가</span>
            <input
              v-model="addUsageCount"
              type="number"
              min="1"
              placeholder="인원 수"
              class="cell-input usage-add__input"
            />
            <v-btn
              @click="addUsageStudents()"
              variant="outlined"
              size="small"
              class="btn-secondary"
            >추가</v-btn>
            <v-btn
              v-if="contractDirty"
              @click="updateSchoolContract()"
              variant="flat"
              size="small"
              class="btn-primary"
            >저장</v-btn>
          </div>
        </div>
      </template>

      <div class="table-wrap">
        <table class="data-table">
          <tr>
            <td class="th" style="width: 150px">계정 생성일</td>
            <td class="th" style="width: 150px">계약 시작일</td>
            <td class="th" style="width: 150px">계약 만료일</td>
            <td class="th" style="width: 90px">계약 학생수</td>
            <td class="th" style="width: 90px">현재 학생수</td>
            <td class="th" style="width: 90px">학급수</td>
            <td class="th tdlast" style="width: 320px">
              <label class="vacation-checkbox">
                <span>방학 중지</span>
                <input type="checkbox" v-model="vacationEnabled" />
              </label>
            </td>
          </tr>
          <tr v-for="(j, i) in contracts" :key="i">
            <td class="td">{{ accountIssueDate }}</td>
            <td
              @click.stop="viewdateon(0, $event, i)"
              class="td td-clickable"
              style="width: 150px"
            >
              <div class="td-select">
                <span>{{ datetext(j.startDate) }}</span>
                <span class="td-caret" aria-hidden="true">▼</span>
              </div>
            </td>
            <td
              @click.stop="viewdateon(1, $event, i)"
              class="td td-clickable"
              style="width: 150px"
            >
              <div class="td-select">
                <span>{{ datetext(j.endDate) }}</span>
                <span class="td-caret" aria-hidden="true">▼</span>
              </div>
            </td>
            <td class="td">{{ j.contractStudentNumber }}</td>
            <td class="td">{{ j.currentStudentNumber }}</td>
            <td class="td" style="width: 90px">{{ j.classCount }}</td>
            <td class="td tdlast" style="width: 320px">
              <div v-if="j.vacation" class="vacation-dates">
                <input type="date" v-model="j.vacationStartDate" class="cell-input" />
                <span>~</span>
                <input type="date" v-model="j.vacationEndDate" class="cell-input" />
              </div>
            </td>
          </tr>
        </table>
      </div>
    </FormCard>

    <!-- 학급 정보 -->
    <FormCard class="section-card">
      <template #header>
        <div class="card-header-row">
          <span>학급 정보</span>
          <div class="card-header-actions">
            <!-- 학교용 총괄(엑셀 F열 이름별 발급, 전달 메일 포함 대상) — 발급 학급 전체를
                 담당하는 LMS 계정으로 판정. 기존 masterLmsId 바인딩은 엑셀 플로우가 기록하지
                 않는 필드라 미표시였음 (qa-incomplete-1021 #4) -->
            <span class="master-account-badge">
              총괄 계정
              <span v-if="schoolMasterBadgeText" class="mono">{{ schoolMasterBadgeText }}</span>
              <span v-else class="mono">-</span>
            </span>
            <v-btn
              @click="teacherInfoModalOpen = true"
              variant="outlined"
              size="small"
              class="btn-secondary"
            >교사 정보</v-btn>
          </div>
        </div>
      </template>
      <div class="table-wrap">
        <table class="data-table">
          <tr>
            <td class="th">년도</td>
            <td class="th">학년</td>
            <td class="th">반</td>
            <td class="th">출판사</td>
            <td class="th">학생수</td>
            <td class="th">영어 교사</td>
            <td class="th">담임 교사</td>
            <td class="th tdlast">학생 계정</td>
          </tr>
          <tr v-for="(j, i) in classRows" :key="i">
            <td class="td">{{ j.year }}</td>
            <td class="td">{{ j.grade }}</td>
            <td class="td">{{ j.classNum }}</td>
            <td class="td">{{ j.publisher }}</td>
            <td class="td">{{ j.studentCnt }}</td>
            <td v-if="j.engSpan" :rowspan="j.engSpan" class="td">
              <div class="teacher-split">
                <span>{{ j.engTeacherNameView }}</span>
                <span class="mono">{{ j.engLmsView }}</span>
              </div>
            </td>
            <td class="td">
              <div class="teacher-split">
                <span>{{ j.teacherNameView }}</span>
                <span class="mono">{{ j.homeroomLmsView }}</span>
              </div>
            </td>
            <td class="td tdlast">
              <button
                @click="viewclassdetail(j.grade, j.classNum, i)"
                class="iddetailbtn"
                :class="{ iddetailbtnselected: popupindex === i }"
                ref="target"
              >
                •&nbsp;•&nbsp;•
              </button>
              <div class="classpopup-anchor">
                <classpopup
                  v-if="idlist.length > 0 && popupindex === i"
                  :classInfo="idlist"
                  @closeEvent="popupClose()"
                ></classpopup>
              </div>
            </td>
          </tr>
        </table>
      </div>
    </FormCard>

    <!-- 교사 정보 —상단 "교사 정보" 버튼을 눌러야 팝업으로 열림 -->
    <!-- 메모 기록 -->
    <v-dialog v-model="memoHistoryOpen" max-width="520">
      <div class="memo-history">
        <div class="memo-history__title">메모 기록</div>
        <div v-if="memoHistory.length" class="memo-history__list">
          <div v-for="(h, i) in memoHistory" :key="i" class="memo-history__item">
            <span class="memo-history__date">{{ h.date }}</span>
            <span class="memo-history__text">{{ h.memo || "(빈 메모)" }}</span>
          </div>
        </div>
        <div v-else class="memo-history__empty">저장된 메모 기록이 없습니다.</div>
      </div>
    </v-dialog>

    <v-dialog v-model="teacherInfoModalOpen" width="auto">
      <contactcomponent
        v-if="teacherInfoModalOpen"
        :contactlist="school.contact"
        :contactlist_origin="school.contact"
      ></contactcomponent>
    </v-dialog>

    <!-- 계정발급 상태 드롭다운 -->
    <Teleport to="body">
      <div v-if="accountopen && accountPos" class="teleport-dropdown" :style="tdropPos(accountPos, 160)" @click.stop>
        <div v-for="(opt, idx) in accountoptions" :key="idx" class="teleport-dropdown__item" @click="modifyaccountstatus(opt)">
          <StatusBadge :status="opt" :dot="false" variant="text" />
        </div>
      </div>
    </Teleport>

    <!-- 분류 드롭다운 — 학교 조회 탭 분류의 기준이 되는 값 -->
    <Teleport to="body">
      <div v-if="typeopen && typePos" class="teleport-dropdown" :style="tdropPos(typePos, 200)" @click.stop>
        <div
          v-for="(opt, idx) in typeoptions"
          :key="idx"
          class="teleport-dropdown__item"
          @click="modifytype(opt)"
        >
          <p>{{ opt || "유료" }}</p>
        </div>
      </div>
    </Teleport>

    <!-- 요금제 드롭다운 -->
    <Teleport to="body">
      <div v-if="planChangeFlag && planPos" class="teleport-dropdown" :style="tdropPos(planPos, 120)" @click.stop>
        <div v-for="(j, idx) in plans" :key="idx" class="teleport-dropdown__item" @click="changePlan(j)">
          <p>{{ displayPlan(j) }}</p>
        </div>
      </div>
    </Teleport>

    <!-- 계약 시작일 DatePicker -->
    <Teleport to="body">
      <div
        v-if="viewDate[0] && datePos[0] && contracts[contractEditIdx]"
        class="teleport-datepicker"
        :style="{ top: datePos[0].top + 'px', left: datePos[0].left + 'px' }"
        @click.stop
      >
        <DatePicker
          :max-date="contracts[contractEditIdx].endDate"
          v-model="contracts[contractEditIdx].startDate"
          color="blue"
        />
      </div>
    </Teleport>

    <!-- 계약 만료일 DatePicker -->
    <Teleport to="body">
      <div
        v-if="viewDate[1] && datePos[1] && contracts[contractEditIdx]"
        class="teleport-datepicker"
        :style="{ top: datePos[1].top + 'px', left: datePos[1].left + 'px' }"
        @click.stop
      >
        <DatePicker
          :min-date="contracts[contractEditIdx].startDate"
          v-model="contracts[contractEditIdx].endDate"
          color="blue"
        />
      </div>
    </Teleport>
  </div>
</template>

<script>
import contactcomponent from "../schoolDetail/ContactComponent.vue";
import classpopup from "../../../popup/ClassInfoPopupComponent2.vue";
import { DatePicker } from "v-calendar";
import dayjs from "dayjs";
import { changeSchoolPayPlan } from "@/api/api-list";
import {
  createMasterLms,
  resetTeacherRegistration,
  resetAccountPassword,
} from "@/api/user-api-list";
import { PageHeader, FormCard, StatusBadge } from "@/components/ui";
import { SCHOOL_TYPE_OPTIONS, OFFICE_OPTIONS } from "@/constants/schoolType";

const OFFICE_SHORT_NAME = {
  "서울특별시교육청": "서울",
  "경기도교육청": "경기",
  "충청남도교육청": "충남",
  "경상남도교육청": "경남",
  "전라북도교육청": "전북",
  "경상북도교육청": "경북",
  "인천광역시교육청": "인천",
  "부산광역시교육청": "부산",
  "대구광역시교육청": "대구",
  "전라남도교육청": "전남",
  "대전광역시교육청": "대전",
  "충청북도교육청": "충북",
  "광주광역시교육청": "광주",
  "강원도교육청": "강원",
  "울산광역시교육청": "울산",
  "세종특별자치시교육청": "세종",
  "제주특별자치도교육청": "제주",
  "베트남교육청": "베트남",
};

export default {
  async created() {
    const code = this.$route.params.code;
    const res = await this.$store.dispatch("GET_SCHOOL_DETAIL", code);
    const res2 = await this.$store.dispatch("GET_SCHOOL_CLASS_LIST", code);
    this.contracts = await this.$store.dispatch(
      "GET_SCHOOL_CONTRACT_INFO",
      code,
    );

    this.school = res || {};
    this.payStatus = (res && res.payStatus) || "";
    this.accountStatus = (res && res.accountStatus) || "";
    this.classlist = res2 || [];
    this.originalPayPlan = this.school.payPlan;
    this.originalAccountStatus = this.school.accountStatus;
    this.originalType = this.school.type ?? "";
    this.originalMemo = this.school.memo ?? "";
    this.loadMemoHistory();
    this.originalOffice = this.school.office ?? "";
    this.originalSchoolName = this.school.schoolName ?? "";
    this.contractSnapshot = JSON.stringify(this.contracts);
    // [3차] 모든 비동기 로드 완료 후에야 본문 마운트 허용
    this.schoolLoaded = true;
  },
  data() {
    return {
      schoolLoaded: false,
      school: {},
      payStatus: "",
      accountStatus: "",
      classlist: [],
      idlist: [],
      idmodal: false,
      contracts: {},
      viewDate: [false, false],
      originalPayPlan: "",
      originalAccountStatus: "",
      originalType: "",
      originalMemo: "",
      memoHistoryOpen: false,
      // 메모 변경 기록 — 최신순 [{ date, memo }].
      // TODO: 백엔드 저장 전까지는 이 브라우저(localStorage)에만 남음 → 다른 PC/관리자와 공유 안 됨
      memoHistory: [],
      originalOffice: "",
      originalSchoolName: "",
      officeOptions: OFFICE_OPTIONS,
      contractSnapshot: "",
      accountoptions: ["안내필요", "정보대기", "발급필요", "발급완료"],
      accountopen: false,
      // 맨 앞 ""(빈값) = "유료" 표시 — 특수 분류를 해제해 일반 유료로 되돌리는 선택지.
      // 저장은 빈값으로 되므로 DB 마이그레이션 없이 동작한다 (B안).
      typeoptions: ["", ...SCHOOL_TYPE_OPTIONS],
      typeopen: false,
      typePos: null,
      popupindex: -1,
      resetPasswordId: "",
      // 총괄 계정 발급 — 신청 없이 학교의 총괄 계정만 발급하고 싶을 때 사용.
      // 아직 발급 API 연동 전, UI만 우선 추가.
      masterAccountName: "",
      // 선생님 정보 초기화 — 교사 LMS 로그인 시 등록한 개인정보/휴대폰 인증/비밀번호를
      // 초기화해 재등록할 수 있게 하는 기능. 아직 발급 API 연동 전, UI만 우선 추가.
      resetTeacherInfoId: "",
      // 교사 정보 팝업 — 상단 헤더 자리로 옮기면서 항상 펼쳐져 있던 걸 클릭 시에만 열리게 함
      teacherInfoModalOpen: false,
      addUsageCount: "",
      plans: ["general", "premium", "m", "sm", "pm"],
      planChangeFlag: false,
      planUpdate: "",
      accountPos: null,
      planPos: null,
      datePos: [null, null],
      contractEditIdx: 0,
    };
  },
  mounted() {
    this._closeDropdowns = () => {
      this.accountopen = false;
      this.typeopen = false;
      this.planChangeFlag = false;
      this.viewDate = [false, false];
    };
    window.addEventListener('click', this._closeDropdowns);
  },
  beforeUnmount() {
    window.removeEventListener('click', this._closeDropdowns);
  },
  computed: {
    // 학급 정보 표시용 — 교사 이름 | LMS 계정 분리 표기.
    // 응답 필드 의미(유림초 실데이터로 확인):
    //   engTeacher / engTeacherName = 그 반을 LMS에 등록한 교사(= 담임) 계정·이름
    //   teacherName                  = 그 반의 영어 교사 이름 (여러 반 공통)
    // 영어 교사 LMS 계정은 반별로 오지 않으므로, 학교 전체 반 중
    // engTeacherName이 영어 교사 이름과 같은 반의 engTeacher를 찾아 채움.
    // 영어 교사 본인이 등록자로 잡힌 반은 담임 정보가 없는 것으로 보고 "-" 처리.
    classRows() {
      const pickName = (t) => (typeof t === "object" && t ? t.name : t) || "";
      const lmsByName = {};
      this.classlist.forEach((c) => {
        const n = pickName(c.engTeacherName);
        if (n && c.engTeacher && !lmsByName[n]) lmsByName[n] = c.engTeacher;
      });
      const rows = this.classlist.map((c) => {
        const engName = pickName(c.teacherName);
        const regName = pickName(c.engTeacherName);
        const isHomeroom = regName && regName !== engName;
        return {
          ...c,
          engTeacherNameView: engName || "-",
          engLmsView: (engName && lmsByName[engName]) || "-",
          teacherNameView: isHomeroom ? regName : "-",
          homeroomLmsView: (isHomeroom && c.engTeacher) || "-",
        };
      });
      // 연속된 행의 영어 교사가 같으면 셀 병합 — 첫 행에 rowspan, 나머지는 0(셀 생략).
      // 교사 정보가 없는 행("-")끼리는 병합하지 않음.
      const engKey = (r) => `${r.engTeacherNameView}|${r.engLmsView}`;
      rows.forEach((r, i) => {
        const prev = rows[i - 1];
        if (prev && r.engTeacherNameView !== "-" && engKey(prev) === engKey(r)) {
          r.engSpan = 0;
          return;
        }
        let span = 1;
        while (
          r.engTeacherNameView !== "-" &&
          rows[i + span] &&
          engKey(rows[i + span]) === engKey(r)
        ) span++;
        r.engSpan = span;
      });
      return rows;
    },
    schoolTitle() {
      const location = this.school?.location || OFFICE_SHORT_NAME[this.school?.office] || "";
      const rawName = this.school?.schoolName;
      const name = rawName ? `${rawName}초` : "학교 상세";
      const code = this.school?.schoolCode;
      const parts = [location, name].filter(Boolean).join(" ");
      return code ? `${parts} ${code}` : parts;
    },
    schoolInfoDirty() {
      return (
        this.school.payPlan !== this.originalPayPlan ||
        this.school.accountStatus !== this.originalAccountStatus ||
        (this.school.type ?? "") !== this.originalType ||
        (this.school.memo ?? "") !== this.originalMemo ||
        (this.school.office ?? "") !== this.originalOffice ||
        (this.school.schoolName ?? "") !== this.originalSchoolName
      );
    },
    contractDirty() {
      return JSON.stringify(this.contracts) !== this.contractSnapshot;
    },
    // 상단 요약(플랜/계정발급상태/분류/메모)에 함께 보여줄 올해 계약 학생 수 —
    // 아래 "계약 정보" 테이블(this.contracts)에서 현재 연도 행을 찾아 가져온다.
    currentYearContractStudentNumber() {
      const list = this.contracts || [];
      const year = new Date().getFullYear();
      const match = list.find((c) => c.year == year);
      return match ? match.contractStudentNumber : null;
    },
    // 헤더 체크박스 하나로 모든 계약 행의 방학 중지를 함께 켜고 끈다.
    vacationEnabled: {
      get() {
        const list = this.contracts || [];
        return list.length > 0 && list.every((c) => c.vacation);
      },
      set(value) {
        (this.contracts || []).forEach((c) => {
          c.vacation = value;
        });
      },
    },
    // 학교용 총괄 배지 텍스트 — "이름 (id)" 나열, 여러 명이면 쉼표 구분
    schoolMasterBadgeText() {
      const list = this.school?.masterAccounts?.school || [];
      if (!list.length) return "";
      return list.map((a) => `${a.name} (${a.id})`).join(", ");
    },
    accountIssueDate() {
      // 학교 등록(생성) 시각 = 계정 발급 일자. createdAt → _id timestamp 순으로 fallback
      // (allschool.vue createdAtMillis()와 동일한 규칙)
      let millis = 0;
      if (this.school?.createdAt) {
        const t = new Date(this.school.createdAt).getTime();
        if (!Number.isNaN(t)) millis = t;
      }
      if (!millis && typeof this.school?._id === "string" && this.school._id.length >= 8) {
        const secs = parseInt(this.school._id.substring(0, 8), 16);
        if (Number.isFinite(secs)) millis = secs * 1000;
      }
      return millis ? this.datetext(millis) : "-";
    },
  },
  components: {
    contactcomponent,
    DatePicker,
    classpopup,
    PageHeader,
    FormCard,
    StatusBadge,
  },
  methods: {
    calcPos(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      return { top: rect.bottom + 4, left: rect.left + rect.width / 2 };
    },
    tdropPos(pos, minWidth) {
      return {
        top: pos.top + 'px',
        left: (pos.left - minWidth / 2) + 'px',
        minWidth: minWidth + 'px',
      };
    },
    displayPlan(plan) {
      if (!plan) return "-";
      if (plan === "premium") return "p";
      if (plan === "general") return "s";
      return plan;
    },
    popupClose() {
      this.popupindex = -1;
      this.idlist = [];
    },
    planChangePopup(event) {
      this.planChangeFlag = !this.planChangeFlag;
      if (this.planChangeFlag && event) this.planPos = this.calcPos(event);
    },
    async changePlan(payPlan) {
      this.school.payPlan = payPlan;
      this.planChangeFlag = false;
    },
    async viewclassdetail(grade, classNum, index) {
      this.idlist = [];
      const schoolCode = this.$route.params.code;
      const data = { schoolCode, grade, classNum };
      const res = await this.$store.dispatch("GET_SCHOOL_CLASS_ID_LIST", data);
      this.idlist = res.sort((a, b) => a.attendanceNumber - b.attendanceNumber);
      this.popupindex = index;
    },
    offclassdetail() {
      this.idmodal = false;
      this.idlist = [];
    },
    addUsageStudents() {
      const count = Number(this.addUsageCount);
      if (!count || count <= 0) {
        alert("추가할 인원 수를 입력해주세요.");
        return;
      }
      // 추가 인원은 "계약 학생 수"(contractStudentNumber)에 반영한다 — 스웨거의
      // PUT /transfer/add와 동일한 동작. 기존엔 현재 학생수만 올려 계약 수에
      // 반영되지 않았음 (qa-incomplete-1021 #2). 저장 버튼으로 update/info/contract에
      // 전달되어 영속화된다.
      (this.contracts || []).forEach((c) => {
        c.contractStudentNumber = (Number(c.contractStudentNumber) || 0) + count;
      });
      this.addUsageCount = "";
    },
    async issueMasterAccount() {
      const schoolCode = this.$route.params.code;
      const name = (this.masterAccountName || "").trim();
      if (!window.confirm(`총괄 계정을 발급하시겠습니까? (이름: ${name || "총괄"})`)) return;

      const res = await createMasterLms(schoolCode, name);
      const data = res?.data?.data;
      if (data === -1) {
        alert("학교를 찾을 수 없습니다.");
      } else if (data === -2) {
        alert("발급된 학급이 없어 총괄 계정을 만들 수 없습니다. 학생/교사 계정을 먼저 발급해주세요.");
      } else if (data && data.id) {
        alert(
          data.updated
            ? `이미 총괄 계정(${data.id})이 있어 담당 학급만 최신으로 갱신했습니다. (${data.classCount}개 반)`
            : `총괄 계정이 발급되었습니다.\nID: ${data.id} / 비밀번호: 000000 (${data.classCount}개 반)`
        );
        this.masterAccountName = "";
      } else {
        alert("총괄 계정 발급 중 오류가 발생했습니다.");
      }
    },
    async resetTeacherInfo() {
      const id = (this.resetTeacherInfoId || "").trim();
      if (!id) {
        alert("초기화할 교사/LMS 계정 ID를 입력해주세요.");
        return;
      }
      if (
        !window.confirm(
          `${id} 계정의 등록 정보(이름/전화/이메일/약관동의)와 비밀번호(000000)를 초기화하시겠습니까?\n다음 로그인 시 정보 등록 화면이 다시 표시됩니다.`
        )
      )
        return;

      const res = await resetTeacherRegistration(id);
      const data = res?.data?.data;
      if (data === -1) {
        alert("해당 ID의 계정을 찾을 수 없습니다.");
      } else if (data === -2) {
        alert("교사/LMS 계정만 초기화할 수 있습니다. (학생 계정 불가)");
      } else if (data && data.reset) {
        alert(`초기화되었습니다.\nID: ${data.id} / 비밀번호: 000000`);
        this.resetTeacherInfoId = "";
      } else {
        alert("초기화 중 오류가 발생했습니다.");
      }
    },
    async resetStudentPassword() {
      const id = this.resetPasswordId.trim();
      if (!id) {
        alert("초기화할 ID를 입력해주세요.");
        return;
      }
      if (!window.confirm(`${id} 계정의 비밀번호를 000000으로 초기화하시겠습니까?`)) return;
      const res = await resetAccountPassword(id);
      const data = res?.data?.data;
      if (data === -1) {
        alert("해당 ID의 계정을 찾을 수 없습니다.");
      } else if (data && data.reset) {
        alert("비밀번호가 000000으로 초기화되었습니다.");
        this.resetPasswordId = "";
      } else {
        alert("비밀번호 초기화에 실패하였습니다. 잠시 후 다시 시도해주세요.");
      }
    },
    memoHistoryKey() {
      return `memoHistory:${this.$route.params.code}`;
    },
    loadMemoHistory() {
      let saved = [];
      try {
        saved = JSON.parse(localStorage.getItem(this.memoHistoryKey()) || "[]");
      } catch (e) {
        saved = [];
      }
      // 서버 기록(schools.memoHistory, [{ at: ISO, memo }], 최신이 앞)이 있으면 우선 사용.
      // 표시 형식(YYYYMMDD)에 맞춰 at → date 로 변환한다. 없으면 이 브라우저의 localStorage 기록으로 폴백.
      const server = Array.isArray(this.school.memoHistory)
        ? this.school.memoHistory.map((h) => ({
            date: h.at ? this.formatMemoDate(new Date(h.at)) : h.date || "-",
            memo: h.memo ?? "",
          }))
        : [];
      this.memoHistory = server.length ? server : saved;
      // 기록이 하나도 없으면 현재 메모를 첫 기록으로 남겨둠
      // 기록이 없으면 현재 메모를 학교 정보 최종 수정일 기준으로 첫 기록으로 표시
      if (!this.memoHistory.length && this.school.memo) {
        const saved = this.school.memoUpdatedAt || this.school.updatedAt;
        this.memoHistory = [
          { date: saved ? this.formatMemoDate(new Date(saved)) : "-", memo: this.school.memo },
        ];
      }
    },
    formatMemoDate(d) {
      if (isNaN(d)) return "-";
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
    },
    addMemoHistory(memo) {
      const date = this.formatMemoDate(new Date());
      this.memoHistory = [{ date, memo }, ...this.memoHistory];
      try {
        localStorage.setItem(this.memoHistoryKey(), JSON.stringify(this.memoHistory));
      } catch (e) {
        /* 저장 불가 환경은 무시 */
      }
    },
    async updateSchoolInfo() {
      if (window.confirm("학교 정보를 업데이트 하시겠습니까?")) {
        const schoolCode = this.$route.params.code;
        const {
          office,
          schoolName,
          payType,
          payStatus,
          accountStatus,
          affiliate,
          language,
          payPlan,
          type,
          memo,
        } = this.school;
        const data = {
          office,
          schoolCode,
          schoolName,
          payType: payType || "-",
          payStatus: payStatus || "-",
          accountStatus: accountStatus || "-",
          affiliate: affiliate || "-",
          language,
          // 분류는 ''(미분류)로 되돌리는 것도 유효한 변경이므로 그대로 전달한다.
          type: type ?? "",
        };
        const res = await this.$store.dispatch("UPDATE_SCHOOL_INFO", data);
        await changeSchoolPayPlan({ schoolCode, payPlan });
        if ((memo ?? "") !== this.originalMemo) {
          await this.$store.dispatch("UPDATE_SCHOOL_MEMO", {
            schoolCode,
            memo: memo ?? "",
          });
          this.addMemoHistory(memo ?? "");
        }
        if (res) {
          alert("학교 정보가 업데이트 되었습니다.");
          this.payStatus = payStatus;
          this.accountStatus = accountStatus;
          this.originalPayPlan = payPlan;
          this.originalAccountStatus = accountStatus;
          this.originalType = type ?? "";
          this.originalMemo = memo ?? "";
          this.originalOffice = office ?? "";
          this.originalSchoolName = schoolName ?? "";
        } else {
          alert("학교 정보 업데이트 중 오류가 발생하였습니다.");
          this.school.payPlan = this.originalPayPlan;
          this.school.accountStatus = this.originalAccountStatus;
          this.school.type = this.originalType;
          this.school.memo = this.originalMemo;
          this.school.office = this.originalOffice;
          this.school.schoolName = this.originalSchoolName;
        }
      }
    },
    async updateSchoolContract() {
      if (window.confirm("계약 정보를 업데이트 하시겠습니까?")) {
        const schoolCode = this.$route.params.code;
        const contractList = this.contracts;
        for (let i = 0; i < contractList.length; i++) {
          contractList[i].startDate = this.datetext(contractList[i].startDate);
          contractList[i].expiryDate = this.datetext(contractList[i].endDate);
          contractList[i].year = new Date().getFullYear();
        }
        const data = { schoolCode, contractList };
        const res = await this.$store.dispatch(
          "UPDATE_SCHOOL_CONTRACT_INFO",
          data,
        );
        if (res) {
          alert("계약 정보가 업데이트 되었습니다.");
          this.contractSnapshot = JSON.stringify(this.contracts);
        } else {
          alert("계약 정보 업데이트 중 오류가 발생하였습니다.");
        }
      }
    },
    viewdateon(index, event, rowIdx) {
      if (rowIdx !== undefined) this.contractEditIdx = rowIdx;
      const otherIndex = index === 0 ? 1 : 0;
      this.viewDate[otherIndex] = false;
      this.viewDate[index] = !this.viewDate[index];
      if (this.viewDate[index] && event) {
        const pos = this.calcPos(event);
        const newDatePos = [...this.datePos];
        newDatePos[index] = pos;
        this.datePos = newDatePos;
      }
    },
    datetext(str) {
      return dayjs(str).format("YYYY/MM/DD");
    },
    goaccount(event) {
      this.typeopen = false;
      this.planChangeFlag = false;
      this.accountopen = !this.accountopen;
      if (this.accountopen && event) this.accountPos = this.calcPos(event);
    },
    gotype(event) {
      this.accountopen = false;
      this.planChangeFlag = false;
      this.typeopen = !this.typeopen;
      if (this.typeopen && event) this.typePos = this.calcPos(event);
    },
    modifytype(type) {
      this.school.type = type;
      this.typeopen = false;
    },
    modifyaccountstatus(status) {
      this.school.accountStatus = status;
      this.accountopen = false;
    },
  },
  watch: {
    payStatus() {
      this.accountopen = false;
    },
    accountStatus() {
      this.accountopen = false;
    },
  },
};
</script>

<style scoped>
.page-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.section-card {
  /* FormCard 기본 사용 */
}

.header-info-row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-5);
  flex-wrap: wrap;
}

.header-info-row :deep(.page-header) {
  flex-shrink: 0;
  margin-bottom: 0;
}

.school-info-inline {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.school-info-table {
  min-width: 0 !important;
}

.school-info-table .th,
.school-info-table .td {
  padding: 4px 8px;
  font-size: var(--font-small-size);
  white-space: nowrap;
}

/* 교육청/학교명/메모 — 내용 길이만큼 칸이 늘어나 잘리지 않게 */
.school-info-table .cell-input {
  field-sizing: content;
  width: auto;
  min-width: 80px;
}

.memo-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.memo-history-btn {
  flex-shrink: 0;
  padding: 0 4px;
  font-size: 10px;
  line-height: 16px;
  color: var(--text-secondary);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-surface-raised);
  cursor: pointer;
}
.memo-history {
  padding: var(--space-5);
  background: var(--bg-surface, #fff);
  border-radius: var(--radius-md, 8px);
}
.memo-history__title {
  font-weight: 600;
  margin-bottom: var(--space-3);
}
.memo-history__list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-height: 400px;
  overflow-y: auto;
}
.memo-history__item {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--border-default);
  font-size: var(--font-small-size);
}
.memo-history__date {
  color: var(--text-tertiary);
}
.memo-history__text {
  white-space: pre-wrap;
  word-break: break-all;
}
.memo-history__empty {
  color: var(--text-tertiary);
  font-size: var(--font-small-size);
}

.school-info-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.detail-actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-left: auto;
  flex-shrink: 0;
}

.title-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  flex-shrink: 0;
}

.title-line {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.teacher-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
}
.teacher-split > span:first-child {
  border-right: 1px solid var(--border-default);
}

.card-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.card-header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.master-account-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2);
  font-size: var(--font-small-size);
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-subtle);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
}
.master-account-badge .mono {
  font-family: var(--font-family-mono, "JetBrains Mono", "D2Coding", monospace);
  color: var(--text-primary);
  font-weight: 600;
}

.password-reset {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.password-reset__label {
  font-size: var(--font-small-size);
  color: var(--text-secondary);
  white-space: nowrap;
}
/* detail-actions(총괄 계정 발급/비밀번호 변경/선생님 정보 초기화) 3줄의 라벨
   길이가 서로 달라 입력창/버튼 위치가 어긋나 보이던 것을, 가장 긴 라벨
   ("선생님 정보 초기화") 기준으로 폭을 고정해 맞춘다. */
.detail-actions .password-reset__label {
  min-width: 100px;
}

.usage-add__label {
  font-size: var(--font-small-size);
  color: var(--text-secondary);
  white-space: nowrap;
}

.usage-add__input.cell-input {
  width: 72px;
  flex: 0 0 auto;
  padding-left: 4px;
  padding-right: 4px;
  text-align: center !important;
  border-color: var(--border-default);
  background: var(--bg-surface-raised);
  /* 숫자 입력 스피너(위아래 화살표)가 박스 오른쪽 공간을 차지해서 텍스트가
     시각적으로 왼쪽에 치우쳐 보이던 것 — 스피너를 없애 진짜 가운데 정렬되게 함 */
  -moz-appearance: textfield;
  appearance: textfield;
}
.usage-add__input.cell-input::-webkit-outer-spin-button,
.usage-add__input.cell-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.password-reset .cell-input {
  width: 160px;
  border-color: var(--border-default);
  background: var(--bg-surface-raised);
}

.btn-primary {
  background: var(--color-primary-600) !important;
  color: var(--text-on-primary) !important;
}

.btn-secondary {
  color: var(--text-primary) !important;
  border-color: var(--border-strong) !important;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.data-table {
  text-align: center;
  border-spacing: 0;
  min-width: 600px;
  width: 100%;
  font-size: var(--font-body-size);
  line-height: var(--font-body-line);
  color: var(--text-primary);
}

.th {
  border: 1px solid var(--border-default);
  border-right: none;
  padding: var(--space-3) var(--space-2);
  font-size: var(--font-small-size);
  background-color: var(--bg-subtle);
  color: var(--text-secondary);
  font-weight: 600;
}

.td {
  border: 1px solid var(--border-default);
  border-right: none;
  border-top: none;
  padding: var(--space-3) var(--space-2);
  font-size: var(--font-body-size);
  color: var(--text-primary);
}

.tdlast {
  border-right: 1px solid var(--border-default);
}

.td-clickable {
  cursor: pointer;
  position: relative;
}

.td-disabled {
  cursor: default;
  color: var(--text-tertiary);
}

.td-select {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  width: 100%;
  text-align: center;
}

.td-caret {
  font-size: 9px;
  color: var(--text-tertiary);
}

.td-dropdown {
  position: absolute;
  z-index: 50;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: var(--space-1);
  min-width: 160px;
  background-color: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.td-dropdown--narrow {
  min-width: 140px;
}

.td-dropdown__item {
  padding: var(--space-3) var(--space-3);
  cursor: pointer;
  font-size: var(--font-small-size);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--border-default);
}

.td-dropdown__item:last-child {
  border-bottom: none;
}

.td-dropdown__item:hover {
  background-color: var(--color-primary-50);
}

.cell-input {
  width: 100%;
  padding: var(--space-1) var(--space-2);
  border: 1px solid transparent;
  background: transparent;
  font-size: var(--font-body-size);
  color: var(--text-primary);
  text-align: center;
  border-radius: var(--radius-sm);
}

.cell-input:not(:disabled) {
  border-color: var(--border-default);
  background: var(--bg-surface-raised);
}

.cell-select {
  cursor: pointer;
}

.mono-code {
  font-family: var(--font-family-mono);
  font-size: var(--font-mono-size);
  color: var(--text-primary);
}

.vacation-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  cursor: pointer;
  white-space: nowrap;
}

.vacation-dates {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
}

.vacation-dates .cell-input {
  width: auto;
}

.datepicker-pop {
  position: absolute;
  z-index: 989;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: var(--space-1);
}

.payPlanContent {
  width: 100px;
  background-color: var(--bg-surface-raised);
  position: absolute;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: var(--space-1);
  z-index: 50;
  overflow: hidden;
}

.payPlanContentElement {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: var(--font-small-size);
  color: var(--text-primary);
}

.payPlanContentElement:hover {
  background-color: var(--color-primary-50);
}

.iddetailbtn {
  font-size: var(--font-body-size);
  border: 1px solid var(--border-default);
  font-weight: 600;
  background-color: var(--bg-surface-raised);
  color: var(--text-primary);
  width: 70px;
  height: 32px;
  border-radius: var(--radius-pill);
  cursor: pointer;
}

.iddetailbtnselected,
.iddetailbtn:hover {
  background-color: var(--color-primary-50);
  border-color: var(--color-primary-600);
  color: var(--color-primary-600);
}

.classpopup-anchor {
  position: absolute;
  margin-left: -600px;
}

.teleport-dropdown {
  position: fixed;
  z-index: 9999;
  background-color: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.teleport-dropdown__item {
  padding: var(--space-3);
  cursor: pointer;
  font-size: var(--font-small-size);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--border-default);
}

.teleport-dropdown__item:last-child {
  border-bottom: none;
}

.teleport-dropdown__item:hover {
  background-color: var(--color-primary-50);
}

.teleport-datepicker {
  position: fixed;
  z-index: 9999;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}
</style>
